import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCurrentTheme,
  subscribeTheme,
} from "../theme/applyTheme";

/**
 * Floating anthem player. The Saudi national anthem is loaded and played
 * ONLY while the "national_day" template is the one actually applied.
 * Switching to any other template (or disabling templates) immediately
 * pauses, unloads the file, and removes the player — no leftover playback.
 */

const AUDIO_SRC =
  window.__ND_AUDIO_SRC ||
  process.env.PUBLIC_URL + "/audio/national-anthem.mp3";

const NATIONAL_DAY = "national_day";

const isNationalDayTheme = (theme) =>
  Boolean(theme && theme.enabled && theme.template === NATIONAL_DAY);

const stopAudio = (audio) => {
  if (!audio) return;
  audio.pause();
  audio.removeAttribute("src");
  audio.load();
};

const NationalDayAudio = () => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const unlockHandlerRef = useRef(null);
  const [theme, setTheme] = useState(getCurrentTheme);
  const [playing, setPlaying] = useState(false);
  const [hasAudio, setHasAudio] = useState(true);
  const active = isNationalDayTheme(theme);

  useEffect(() => subscribeTheme(setTheme), []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const clearUnlock = () => {
      if (!unlockHandlerRef.current) return;
      document.removeEventListener("click", unlockHandlerRef.current);
      unlockHandlerRef.current = null;
    };

    if (!active) {
      clearUnlock();
      stopAudio(audio);
      setPlaying(false);
      return clearUnlock;
    }

    audio.src = AUDIO_SRC;
    audio.volume = 0.3;

    const start = () => {
      audio.volume = 0.3;
      return audio.play().then(() => setPlaying(true));
    };

    start().catch(() => {
      // Browser blocked autoplay — wait for the first user gesture, but only
      // while this template is still active.
      const startOnClick = () => {
        if (!isNationalDayTheme(getCurrentTheme())) return;
        start().catch(() => {});
      };
      unlockHandlerRef.current = startOnClick;
      document.addEventListener("click", startOnClick, { once: true });
    });

    return () => {
      clearUnlock();
      stopAudio(audio);
      setPlaying(false);
    };
  }, [active]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !active) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.volume = 0.3;
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [playing, active]);

  return (
    <>
      <audio
        ref={audioRef}
        loop
        preload="none"
        onError={() => {
          if (active) setHasAudio(false);
        }}
      />
      {active && hasAudio ? (
        <button
          onClick={toggle}
          aria-label={playing ? t("Mute") : t("Play anthem")}
          title={playing ? t("Mute") : t("Play anthem")}
          className="nd-audio-btn"
        >
          <span className="nd-audio-icon">{playing ? "🔊" : "🔇"}</span>
          <span className="nd-audio-label">
            {playing ? t("Playing") : t("National Anthem")}
          </span>
          {playing && (
            <span className="nd-audio-bars">
              <span />
              <span />
              <span />
              <span />
            </span>
          )}
        </button>
      ) : null}
    </>
  );
};

export default NationalDayAudio;
